import { PageSectionWorkspace } from '@/components/layout/PageSectionWorkspace';
import { useUserPreferences } from '@/features/settings/UserPreferencesProvider';
import { RefreshCw, ShieldCheck } from 'lucide-react';
import { Badge, Button, Card, EmptyState, ErrorState, PageHeader, Skeleton } from '@helios/ui';
import { hasPermission, PERMISSIONS } from '@helios/authz';
import { useAuth } from '@/features/auth/authContext';
import { useRunSecurityScan, useSecurityCommandCenter } from './security.hooks';

export function SecurityCommandCenterPage() {
  const { permissions } = useAuth();
  const { formatDateTime: formatDate } = useUserPreferences();
  const dashboardQuery = useSecurityCommandCenter();
  const scanMutation = useRunSecurityScan();
  const canAdmin = hasPermission(permissions, PERMISSIONS.PLATFORM_ADMIN);
  const runError = scanMutation.error instanceof Error ? scanMutation.error.message : null;

  if (!canAdmin) {
    return (
      <EmptyState
        icon={<ShieldCheck className="h-5 w-5" aria-hidden="true" />}
        title="Security hardening access required"
        description={`Your role needs ${PERMISSIONS.PLATFORM_ADMIN} to view security controls and scans.`}
      />
    );
  }

  if (dashboardQuery.isLoading) {
    return <Skeleton className="h-96 w-full" />;
  }

  if (dashboardQuery.isError || !dashboardQuery.data) {
    return (
      <ErrorState
        title="Unable to load security hardening"
        description={
          dashboardQuery.error instanceof Error
            ? dashboardQuery.error.message
            : 'Unknown security hardening error'
        }
        action={
          <Button size="sm" variant="outline" onClick={() => dashboardQuery.refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  const data = dashboardQuery.data;

  return (
    <PageSectionWorkspace
      header={
        <PageHeader
          eyebrow="S34 · Security Hardening"
          title="Security Hardening"
          description="Tenant isolation, RBAC, audit, and secrets hygiene controls with audited scan runs against live repo and database posture."
          actions={
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => dashboardQuery.refetch()}>
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                Refresh
              </Button>
              <Button
                size="sm"
                disabled={scanMutation.isPending || !data.permissions.canRunSecurityScan}
                onClick={() => {
                  scanMutation.reset();
                  scanMutation.mutate();
                }}
              >
                <ShieldCheck className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                {scanMutation.isPending ? 'Scanning…' : 'Run security scan'}
              </Button>
            </div>
          }
        />
      }
      toolbar={
        <>
          {runError ? <p className="text-destructive text-sm">{runError}</p> : null}
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Card className="p-3">
              <p className="text-muted-foreground text-xs uppercase tracking-wide">Controls</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {data.summary.controlCount}
              </p>
            </Card>
            <Card className="p-3">
              <p className="text-muted-foreground text-xs uppercase tracking-wide">Categories</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {data.summary.categoryCount}
              </p>
            </Card>
            <Card className="p-3">
              <p className="text-muted-foreground text-xs uppercase tracking-wide">
                Latest pass rate
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {data.summary.latestPassRate == null ? '—' : `${data.summary.latestPassRate}%`}
              </p>
            </Card>
            <Card className="p-3">
              <p className="text-muted-foreground text-xs uppercase tracking-wide">
                Failures in recent history
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {data.summary.openFailedFindings}
              </p>
              <p className="text-muted-foreground mt-1 text-xs">
                Failed results among the 40 most recent findings; earlier failures remain visible
                after a later pass.
              </p>
            </Card>
          </div>
        </>
      }
      sections={[
        {
          id: 'security-controls',
          label: 'Security controls',
          content: (
            <Card className="p-3">
              <h2 className="hx-section-title mb-3 text-sm font-semibold">Security controls</h2>
              <ul className="space-y-2">
                {data.controls.map((control) => (
                  <li
                    key={control.id}
                    className="border-border flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2"
                  >
                    <div>
                      <p className="text-sm font-medium">{control.name}</p>
                      <p className="text-muted-foreground text-xs">{control.description}</p>
                    </div>
                    <Badge variant="outline">{control.category}</Badge>
                  </li>
                ))}
              </ul>
            </Card>
          ),
        },
        {
          id: 'recent-findings',
          label: 'Recent findings',
          content: (
            <Card className="p-3">
              <h2 className="hx-section-title mb-3 text-sm font-semibold">Recent findings</h2>
              {data.recentFindings.length === 0 ? (
                <EmptyState
                  icon={<ShieldCheck className="h-5 w-5" aria-hidden="true" />}
                  title="No findings yet"
                  description="Run a security scan to produce tenant isolation, RBAC, audit, and secrets findings."
                />
              ) : (
                <ul className="space-y-2">
                  {data.recentFindings.slice(0, 12).map((finding) => (
                    <li key={finding.id} className="border-border rounded-lg border px-3 py-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium">{finding.title}</span>
                        <Badge variant={finding.passed ? 'success' : 'critical'}>
                          {finding.passed ? 'PASS' : 'FAIL'}
                        </Badge>
                      </div>
                      <p className="text-muted-foreground mt-1 text-xs">
                        {finding.scanNumber} · {finding.category} · {finding.severity}
                      </p>
                      <p className="text-muted-foreground mt-1 text-xs">{finding.detail}</p>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          ),
        },
        {
          id: 'scan-history',
          label: 'Scan history',
          content: (
            <Card className="p-3">
              <h2 className="hx-section-title mb-3 text-sm font-semibold">Scan history</h2>
              {data.scans.length === 0 ? (
                <EmptyState
                  icon={<ShieldCheck className="h-5 w-5" aria-hidden="true" />}
                  title="No scans yet"
                  description="Run the security scan to create an audited SecurityScanRun."
                />
              ) : (
                <ul className="space-y-2">
                  {data.scans.map((scan) => (
                    <li key={scan.id} className="border-border rounded-lg border px-3 py-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium">{scan.scanNumber}</span>
                        <Badge variant="outline">{scan.status}</Badge>
                      </div>
                      <p className="text-muted-foreground mt-1 text-xs">
                        {scan.summary} · {formatDate(scan.createdAt)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          ),
        },
      ]}
    />
  );
}
